import { Grid, Skeleton } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { AppBox, AppButton, AppTypography, EmailText, PhoneText, SimpleCheckBox, SimpleValueSelect } from 'components'
import { ApiQueryKeys, RoleTypes, UserTypeOptions } from 'globals/constants'
import { getFormattedDate, getMDYFormattedDate } from 'globals/functions'
import { useSnackbar } from 'notistack'
import { FC, useEffect, useMemo, useState } from 'react'
import { AdminUserApi, ShowAlert, UserApi } from 'sdk'

interface Props {
  userId: string
  closeDialog: () => void
  refetch?: () => void
}

export const ManageAccountAccountOwnerCard: FC<Props> = ({ userId, closeDialog, refetch }) => {
  const userApi = new UserApi()
  const adminApi = new AdminUserApi()
  const { enqueueSnackbar } = useSnackbar()
  const {
    mutate: getProfile,
    data,
    isPending: isGettingPersonalData,
  } = useMutation({
    mutationFn: () => userApi.getUserPersonalInfo(userId),
    mutationKey: [ApiQueryKeys.user.getUserPersonalInfo],
    onError: (error) => enqueueSnackbar(error?.message, { variant: 'error' }),
    onSuccess: (data) => {
      setNewStatus(data.Active ? 'active' : 'disabled')
    },
  })
  const [newRole, setNewRole] = useState<RoleTypes>()
  const [newStatus, setNewStatus] = useState<string>()

  const userOldRole = useMemo(() => {
    if (data?.Roles?.includes(RoleTypes.SuperAdmin)) {
      return RoleTypes.SuperAdmin
    } else if (data?.Roles?.includes(RoleTypes.Admin)) {
      return RoleTypes.Admin
    } else {
      return RoleTypes.User
    }
  }, [data])

  useEffect(() => {
    setNewRole(userOldRole)
  }, [userOldRole])

  useEffect(() => {
    getProfile()
  }, [])

  const isStatusChange = useMemo(() => newStatus !== (data?.Active ? 'active' : 'disabled'), [data?.Active, newStatus])
  const isRoleChange = useMemo(() => newRole != userOldRole, [newRole, userOldRole])

  const { mutate: changeRole, isPending: isChangingRole } = useMutation({
    mutationKey: [ApiQueryKeys.admin.changeUserRole],
    mutationFn: () => adminApi.changeUserRole({ UserId: userId, Role: newRole as RoleTypes }),
    onError: (err) => enqueueSnackbar(err.message),
    onSuccess: () => {
      if (refetch) {
        refetch()
      }
    },
  })
  const { mutate: changeStatus, isPending: isChangingStatus } = useMutation({
    mutationKey: [ApiQueryKeys.admin.changeUserStatus],
    mutationFn: () => adminApi.changeUserStatus({ UserId: userId, Active: newStatus === 'active' }),
    onError: (err) => enqueueSnackbar(err.message),
    onSuccess: () => {
      if (refetch) {
        refetch()
      }
    },
  })
  const handleUpdate = () => {
    if (isStatusChange || isRoleChange) {
      closeDialog()
      ShowAlert(
        'question',
        `Are you sure you want to make these changes?<br/> 
        ${isStatusChange ? `Status: ${data?.Active ? 'active' : 'disabled'}  ->  ${newStatus}<br /><br />` : ''}
        ${isRoleChange ? `Role: ${userOldRole}  ->  ${newRole}` : ''}`
      ).then((x) => {
        if (x.isConfirmed) {
          if (isStatusChange) {
            changeStatus()
          }
          if (isRoleChange) {
            changeRole()
          }
        }
      })
    }
  }
  return (
    <AppBox backgroundColor="grey.100" border={1} borderColor={'grey.200'} borderRadius={6} padding={2} width={'100%'}>
      <AppTypography fontWeight={700} fontSize={16}>
        Account Owner
      </AppTypography>
      {isGettingPersonalData ? (
        <Grid container spacing={6}>
          <Grid item xs={6}>
            <AppBox display={'flex'} flexDirection={'column'} gap={1}>
              <Skeleton height={30} width={150} />
              <Skeleton height={30} width={150} />
              <Skeleton height={30} width={150} />
              <Skeleton height={30} width={150} />
            </AppBox>
          </Grid>
          <Grid item xs={6}>
            <AppBox display={'flex'} flexDirection={'column'} gap={1}>
              <Skeleton height={30} width={150} />
              <Skeleton height={30} width={150} />
              <Skeleton height={30} width={150} />
              <Skeleton height={30} width={150} />
            </AppBox>
          </Grid>
        </Grid>
      ) : (
        <>
          <Grid container spacing={6}>
            <Grid item xs={6} rowGap={5} rowSpacing={4}>
              <SimpleValueSelect
                label="User Type:"
                isSmallForm={true}
                size="small"
                options={UserTypeOptions}
                defaultValue={userOldRole}
                value={newRole}
                InputProps={{ style: { backgroundColor: 'white' } }}
                onValueChange={(val) => setNewRole(val as RoleTypes)}
              />
              <AppBox display={'flex'} gap={1}>
                <AppTypography color={'grey.700'}>Account ID:</AppTypography>
                <AppTypography fontWeight={700}>{data?.AccountId}</AppTypography>
              </AppBox>
              <AppBox display={'flex'} gap={1}>
                <AppTypography color={'grey.700'}>Company:</AppTypography>
                <AppTypography fontWeight={700}>{data?.Company}</AppTypography>
              </AppBox>
              <AppBox display={'flex'} gap={1}>
                <AppTypography color={'grey.700'}>Name:</AppTypography>
                <AppTypography fontWeight={700}>{data?.FirstName + ' ' + data?.LastName}</AppTypography>
              </AppBox>
              <AppBox display={'flex'} gap={1}>
                <AppTypography color={'grey.700'}>Address:</AppTypography>
                <AppTypography fontWeight={700}>{data?.Address}</AppTypography>
              </AppBox>
            </Grid>
            <Grid item xs={6}>
              <SimpleValueSelect
                label="Account Access:"
                isSmallForm={true}
                size="small"
                value={newStatus}
                InputProps={{ style: { backgroundColor: 'white' } }}
                options={[
                  { label: 'Active', value: 'active' },
                  { label: 'Disabled', value: 'disabled' },
                ]}
                onValueChange={(val) => {
                  setNewStatus(val.toString())
                }}
              />
              <AppBox display={'flex'} gap={1}>
                <AppTypography color={'grey.700'}>Sign Up Date:</AppTypography>
                <AppTypography fontWeight={700}>
                  {data?.Created ? getMDYFormattedDate(new Date(data?.Created)) : 'N/A'}
                </AppTypography>
              </AppBox>
              <AppBox display={'flex'} gap={1}>
                <AppTypography color={'grey.700'}>Last Login Date:</AppTypography>
                <AppTypography fontWeight={700}>
                  {data?.LastLogin ? getMDYFormattedDate(new Date(data?.LastLogin)) : 'N/A'}
                </AppTypography>
              </AppBox>
              <AppBox display={'flex'} gap={1}>
                <AppTypography color={'grey.700'}>Phone:</AppTypography>
                <PhoneText fontWeight={700} phone={data?.PhoneNumber ?? ''} />
              </AppBox>
              <AppBox display={'flex'} gap={1}>
                <AppTypography color={'grey.700'}>Email:</AppTypography>
                <EmailText email={data?.Email ?? ''} />
              </AppBox>
              <AppBox display={'flex'} gap={1}>
                <AppTypography color={'grey.700'}>Role:</AppTypography>
                <AppTypography fontWeight={700}>{data?.Role}</AppTypography>
              </AppBox>
            </Grid>
          </Grid>
          <AppBox textAlign={'end'} paddingTop={2}>
            <AppButton
              variant="contained"
              size="small"
              sidePaddings={2}
              onClick={handleUpdate}
              disabled={!isStatusChange && !isRoleChange}
              isLoading={isChangingRole || isChangingStatus}
            >
              Update
            </AppButton>
          </AppBox>
        </>
      )}
    </AppBox>
  )
}
